import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGameStore } from '@/hooks/useGameState';
import { useHighScore } from '@/hooks/useHighScore';

interface DeathScreenProps {
  onRevive: () => void;
  onRespawn: () => void;
}

export const DeathScreen = ({ onRevive, onRespawn }: DeathScreenProps) => {
  const { t } = useTranslation();
  const playerSnake = useGameStore((state) => state.playerSnake);
  const { endSession, getPersonalBest } = useHighScore();
  const [scoreResult, setScoreResult] = useState<{ isNewHighScore: boolean; rank: number } | null>(null);
  const [previousBest, setPreviousBest] = useState<number>(0);

  useEffect(() => {
    if (playerSnake && !playerSnake.isAlive) {
      const best = getPersonalBest();
      setPreviousBest(best);
      const result = endSession();
      setScoreResult(result);
    }
  }, [playerSnake?.isAlive]);

  if (!playerSnake) return null;

  const canRevive = !playerSnake.reviveUsed;

  return (
    <div className="game-overlay scanlines z-50">
      <div className="flex flex-col items-center gap-6 p-8 text-center">
        {/* Death message */}
        <h1 className="text-4xl md:text-6xl font-bold text-destructive animate-pulse">
          {t('deathScreen.crashed')}
        </h1>

        {/* Final score */}
        <div className="mt-4">
          <p className="text-muted-foreground text-sm tracking-wider">{t('deathScreen.finalMass')}</p>
          <p className="text-5xl font-bold text-primary neon-text">{playerSnake.score}</p>

          {/* New high score indicator */}
          {scoreResult?.isNewHighScore && (
            <p className="text-secondary text-sm mt-2 animate-pulse tracking-wider">
              {t('deathScreen.newHighScore')}
            </p>
          )}

          {/* Personal best comparison */}
          {!scoreResult?.isNewHighScore && previousBest > 0 && (
            <p className="text-muted-foreground text-xs mt-2">
              {t('deathScreen.personalBest', { score: previousBest })}
            </p>
          )}

          {/* Rank */}
          {scoreResult && scoreResult.rank <= 10 && (
            <p className="text-primary/70 text-xs mt-1">
              {t('deathScreen.leaderboardRank', { rank: scoreResult.rank })}
            </p>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex flex-col gap-4 mt-6 w-full max-w-xs">
          {canRevive && (
            <button
              onClick={onRevive}
              className="neon-button bg-secondary/10 border-secondary text-secondary hover:bg-secondary hover:text-background"
            >
              <span className="block text-lg">{t('deathScreen.revive')}</span>
              <span className="block text-xs opacity-70">{t('deathScreen.keepMass')}</span>
            </button>
          )}

          <button
            onClick={onRespawn}
            className="neon-button"
          >
            <span className="block text-lg">{t('deathScreen.respawn')}</span>
            <span className="block text-xs opacity-70">{t('deathScreen.startFresh')}</span>
          </button>
        </div>

        {/* Tip */}
        <p className="text-muted-foreground text-xs mt-4 max-w-sm">
          {t('deathScreen.tip')}
        </p>
      </div>
    </div>
  );
};
